import {computed, ref} from 'vue';
import {defineStore} from 'pinia';
import type {SubmittedBriefResponse, SubmittedProposalsResponse, ProposalInformationResponse, Origin} from '@api';
import {briefApisService} from '~/composables/briefApisService';
import type {SubmitProposalFormState} from './types';

export const useProposalStore = defineStore('proposal-store', () => {
    const {searchProposalApi} = briefApisService();

    const resumeBriefId = ref<string>();
    const selectedProfileIds = ref<string[]>();
    const currentSearchId = ref<string>();
    const currentProfileViewId = ref<string>();
    const currentOrigin = ref<Origin>();
    const shouldFetchSubmittedProposals = ref(false);

    const submittedProposals = ref<SubmittedProposalsResponse>();
    const proposalInformation = ref<ProposalInformationResponse>();
    const selectedBrief = ref<SubmittedBriefResponse>();

    async function init(
        briefId?: string,
        profileIds?: string[],
        searchId?: string,
        profileViewId?: string,
        origin?: Origin,
        fetchSubmittedProposals = true,
    ) {
        resumeBriefId.value = briefId;
        selectedProfileIds.value = profileIds;
        currentSearchId.value = searchId;
        currentProfileViewId.value = profileViewId;
        currentOrigin.value = origin;
        shouldFetchSubmittedProposals.value = fetchSubmittedProposals;

        await fetchStoreData();
    }

    async function refresh() {
        await fetchStoreData();
    }

    async function fetchStoreData() {
        const promises = [];

        promises.push(
            (async () =>
                (submittedProposals.value = shouldFetchSubmittedProposals.value
                    ? await searchProposalApi.findLatestSubmittedProposals()
                    : {briefs: [], hasNextPage: false}))(),
            (async () => {
                proposalInformation.value = await searchProposalApi.getProposalInformation({
                    briefId: resumeBriefId.value,
                    profileIds: selectedProfileIds.value,
                    searchId: currentSearchId.value,
                });

                // selectedProfileIds may initially be empty if the profiles come from a shortlist
                selectedProfileIds.value = proposalInformation.value.profiles.map((profile) => profile.profileId);
            })(),
        );

        await Promise.all(promises);
    }

    async function loadMoreSubmittedProposals() {
        if (!submittedProposals.value?.hasNextPage) {
            return;
        }

        const latestFetchedBriefId = submittedProposals.value.briefs[submittedProposals.value.briefs.length - 1].value.id;
        const moreSubmittedBriefs = await searchProposalApi.findLatestSubmittedProposals({fromBriefId: latestFetchedBriefId});

        submittedProposals.value = {
            briefs: [...submittedProposals.value.briefs, ...moreSubmittedBriefs.briefs],
            hasNextPage: moreSubmittedBriefs.hasNextPage,
        };
    }

    async function addFreelancersToCurrentBrief(newDescription?: string) {
        if (!selectedBrief.value) {
            throw new Error('Unable to add freelancers to existing brief: no selected brief');
        }

        if (!selectedProfileIds.value) {
            throw new Error('Unable to add freelancers to existing brief: no selected profiles');
        }

        const {headers} = await searchProposalApi.addFreelancersToExistingBriefRaw({
            briefId: selectedBrief.value.value.id,
            addFreelancersToExistingBriefRequest: {
                origin: currentOrigin.value || 'SEARCH_PROPOSAL',
                profileViewId: currentProfileViewId.value,
                description: newDescription ? {value: newDescription} : undefined,
                searchId: currentSearchId.value,
                profileIds: selectedProfileIds.value,
            },
        });

        const redirectLocation = headers.get('Location');

        if (!redirectLocation) {
            throw new Error('addFreelancersToCurrentBrief: no redirect location');
        }

        return {redirectLocation};
    }

    async function submitProposal(proposal: SubmitProposalFormState) {
        if (!selectedProfileIds.value) {
            throw new Error('Unable to submit proposal: no selected profiles');
        }

        const payload = {
            submitProposalRequest: {
                resumeBriefId: resumeBriefId.value,
                profileIds: selectedProfileIds.value,
                origin: currentOrigin.value || 'SEARCH_PROPOSAL',
                searchId: currentSearchId.value,
                profileViewId: currentProfileViewId.value,
                ...proposal,
            },
        };

        const {headers} = await searchProposalApi.submitProposalRaw(payload);

        const redirectLocation = headers.get('Location');

        if (!redirectLocation) {
            throw new Error('submitProposal: no redirect location');
        }

        return {redirectLocation};
    }

    const submittedBriefs = computed<SubmittedBriefResponse[]>(() => submittedProposals.value?.briefs ?? []);
    const canLoadMoreSubmittedBriefs = computed(() => submittedProposals.value?.hasNextPage ?? false);

    return {
        init,
        refresh,
        loadMoreSubmittedProposals,
        canLoadMoreSubmittedBriefs,
        submittedBriefs,
        selectedBrief,
        proposalInformation,
        addFreelancersToCurrentBrief,
        submitProposal,
    };
});
